/**
 * An email or phone link that refuses to render an empty one (HS-875).
 *
 * Every contact field in the API is nullable (`InstitutionViewDTO` declares `required: []`), so a
 * row without a phone number used to produce `<a href="tel:"></a>`: a focusable link with no
 * destination and no accessible name. axe reports it as `link-name`, impact serious, and a
 * screen-reader user meets a link that announces nothing and goes nowhere.
 *
 * The trap this replaces is subtle. The call sites already guarded the *href* (`?? ''`) but not
 * the *content*, which is the half that carries the accessible name — so the code looked
 * defensive while still emitting the defect.
 */
import type { ReactNode } from 'react';

export interface ContactLinkProps {
  /** Chooses the URL scheme. */
  type: 'email' | 'phone';
  value: string | null | undefined;
  className?: string;
  /** Shown instead of a link when there is no value. */
  fallback?: ReactNode;
}

export function ContactLink({ type, value, className, fallback = '—' }: ContactLinkProps) {
  const trimmed = value?.trim();
  if (!trimmed) return <>{fallback}</>;

  // The href keeps the number exactly as stored. Normalising it (stripping spaces and dashes for
  // RFC 3966) is defensible but is a behaviour change nobody asked for here, and every browser
  // dials the formatted form fine — so this component only fixes the empty-link defect.
  return (
    <a href={`${type === 'email' ? 'mailto' : 'tel'}:${trimmed}`} className={className}>
      {trimmed}
    </a>
  );
}
